import React, { useState, useRef, useEffect } from 'react';

const ParticipantDropdown = ({ participants, currentUsername }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	const totalParticipants = participants.length;

	// Sort: PRESENT first, then AWAY, then unknown; alphabetical within each
	const sortedParticipants = [...participants].sort((a, b) => {
		const presenceRank = (p) =>
			p && p.presence_status === 'PRESENT'
				? 0
				: p && p.presence_status === 'AWAY'
				? 1
				: 2;
		const rankA = presenceRank(a);
		const rankB = presenceRank(b);
		if (rankA !== rankB) return rankA - rankB;
		const nameA = (a.username || '').toLowerCase();
		const nameB = (b.username || '').toLowerCase();
		if (nameA < nameB) return -1;
		if (nameA > nameB) return 1;
		return 0;
	});

	return (
		<div className="dropdown" ref={dropdownRef}>
			<button
				className="btn btn-sm btn-outline-secondary d-flex align-items-center"
				type="button"
				onClick={toggleDropdown}
				style={{
					border: '1px solid #dee2e6',
					borderRadius: '4px',
					padding: '4px 8px',
					fontSize: '0.875rem',
				}}
				title="View participants"
			>
				<svg
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="mr-1"
				>
					<path
						d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<circle
						cx="8.5"
						cy="7"
						r="4"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M20 8V14"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M23 11H17"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				{totalParticipants}
			</button>

			{isOpen && (
				<div
					className="dropdown-menu show"
					style={{
						position: 'absolute',
						top: '100%',
						left: '0',
						zIndex: 1000,
						minWidth: '200px',
						padding: '8px',
						border: '1px solid #dee2e6',
						borderRadius: '4px',
						backgroundColor: '#fff',
						boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
						marginTop: '4px',
					}}
				>
					<h6
						className="dropdown-header px-0 mb-2"
						style={{ fontSize: '0.875rem' }}
					>
						Participants ({totalParticipants})
					</h6>

					{/* All participants including current user */}
					{sortedParticipants.map((participant, index) => {
						const isCurrentUser = participant.username === currentUsername;
						const presence = participant.presence_status || 'UNKNOWN';
						const presenceColor =
							presence === 'PRESENT'
								? '#28a745' // green
								: presence === 'AWAY'
								? '#ffc107' // yellow
								: '#6c757d'; // gray / unknown
						const presenceTitle =
							presence === 'PRESENT'
								? 'Active'
								: presence === 'AWAY'
								? 'Idle'
								: 'Unknown';
						return (
							<div
								key={index}
								className="d-flex align-items-center py-1 px-2 mb-1"
								style={{ fontSize: '0.875rem' }}
							>
								<span
									className="status-dot mr-2"
									style={{
										width: '10px',
										height: '10px',
										borderRadius: '50%',
										backgroundColor: presenceColor,
										display: 'inline-block',
									}}
									title={presenceTitle}
								></span>
								<span className={isCurrentUser ? 'font-weight-medium' : ''}>
									{participant.username}
									{isCurrentUser ? ' (You)' : ''}
								</span>
							</div>
						);
					})}

					{participants.length === 0 && (
						<div className="text-muted px-2" style={{ fontSize: '0.875rem' }}>
							No other participants
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ParticipantDropdown;
